/* eslint-disable max-lines */
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import selectors from 'state/selectors';
import MultiSelectInput from 'components/Input/MultiSelect';
import { CommonButton } from 'components/Buttons';
import Toolbar from 'components/Toolbar';
import TooltipControlled from 'components/Tooltip/tooltipControlled';
import DateFilters, { RangeDate } from '../DateFilters';
import { Option } from 'react-multi-select-component';
import utils from 'utils';
import styled from 'styled-components/macro';
import { getCompaniesFromGbsList } from 'routes/ControlTower/utils';
import { GeoCompany, DocumentType, UserCompany, Filter } from 'models/response';
import { URLS } from 'utils/constants';
import { useLocation, useRouteMatch, useHistory } from 'react-router';
import { SummaryKpiFilters } from 'models/request';
import { useTheme } from 'providers/ThemeProvider';
import { useAsync } from 'react-use';
import services from 'services';

const ToolbarWrapper = styled.div``;

const MultiSelectGroup = styled.div`
  display: flex;
  gap: 10px;
  flex: 1;
  min-width: 0;

  > * {
    flex: 1 1 0;
    min-width: 100px;

    > .multi-select-container {
      width: 100% !important;
    }
  }
`;

export const Left = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  gap: 10px;
  padding-left: 10px;
`;
const delayShow = 400;

interface HeaderProps {
  rangeDate?: RangeDate;
  setRangeDate: (rangeDate: RangeDate) => void;
  children?: any;
  gbsList: GeoCompany[];
  setSelectedGbsList: (gbs: Option[]) => void;
  selectedGbsList?: Option[];
  companies: UserCompany[];
  setSelectedCompanies: (company: Option[]) => void;
  selectedCompanies?: Option[];
  types: DocumentType[];
  setSelectedTypes: (type: Option[]) => void;
  selectedTypes?: Option[];
  channels: Option[];
  setSelectedChannels: (channel: Option[]) => void;
  selectedChannels?: Option[];
  handleSearch: () => void;
  handleRefresh?: () => void;
  handleClear?: () => void;
  handleRestoreDefaultFilters?: () => void;
  loadSummaryData?: (params: SummaryKpiFilters) => void;
  setMessage?: (value: string) => void;
}

const Header = (props: HeaderProps) => {
  const t = utils.intl.useTranslator();
  const { path } = useRouteMatch();
  const location = useLocation();
  const history = useHistory();

  const programName = useSelector(selectors.app.getProgramName);

  const [filters, setFilters] = useState<Filter[]>([]);

  const {
    rangeDate,
    setRangeDate,
    gbsList,
    setSelectedGbsList,
    selectedGbsList,
    companies,
    setSelectedCompanies,
    selectedCompanies,
    types,
    setSelectedTypes,
    selectedTypes,
    channels,
    setSelectedChannels,
    selectedChannels,
    handleSearch,
    handleClear,
    handleRestoreDefaultFilters,
    loadSummaryData,
    setMessage,
  } = props;
  const { isDarkMode } = useTheme();

  const isSearchEnabled = rangeDate?.from && rangeDate?.to;

  const isDetailCategoriesPage = location.pathname === `${path}/${URLS.controlTower.detailCategories}`;
  const isChartPage = location.pathname === `${path}/${URLS.controlTower.detailCategories}/${URLS.controlTower.chart}`;

  const { categoryId } = (location?.state as { categoryId?: number }) || {};

  const selectedGeoCompaniesList = getCompaniesFromGbsList(gbsList, selectedGbsList);

  const companiesOptions =
    selectedGbsList?.length && selectedGeoCompaniesList
      ? utils.input.buildOptionsConcatLabel(selectedGeoCompaniesList, ['name', 'socialReason'], 'idCompany')
      : utils.input.buildOptionsConcatLabel(companies, ['name', 'socialReason'], 'idCompany');

  const onChangeSelectedGbsList = (selectedGbsOptions: Option[]) => {
    const companiesFromSelectedGbs = getCompaniesFromGbsList(gbsList, selectedGbsOptions);
    const companiesOptionsFromSelectedGbs = utils.input.buildOptions(companiesFromSelectedGbs, 'name', 'idCompany');

    const selectedGbsListValues = selectedGbsList?.map(({ value }) => value);
    const addedGbsList = selectedGbsOptions.filter(({ value }) => !selectedGbsListValues?.includes(value));
    const addedCompanies = getCompaniesFromGbsList(gbsList, addedGbsList);
    const addedCompaniesOptions = utils.input.buildOptions(addedCompanies, 'name', 'idCompany');

    setSelectedGbsList(selectedGbsOptions);

    const newCompaniesOptions = [...(selectedCompanies || []), ...(addedCompaniesOptions || [])];
    const newCompaniesValues = newCompaniesOptions?.map(({ value }) => value);
    const filteredCompanies = companiesOptionsFromSelectedGbs.filter(({ value }) => newCompaniesValues.includes(value));

    setSelectedCompanies(filteredCompanies);
  };

  useAsync(async () => {
    try {
      const { data } = await services.getKpiFilterConfiguration();
      setFilters(data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  return (
    <ToolbarWrapper>
      <Toolbar.BlueBar style={{ justifyContent: isDetailCategoriesPage || isChartPage ? 'space-between' : 'flex-end' }}>
        {(isDetailCategoriesPage || isChartPage) && (
          <>
            {isChartPage && (
              <CommonButton
                id="back-to-detail-categories"
                action={() =>
                  history.push({
                    pathname: `${path}/${URLS.controlTower.detailCategories}`,
                    state: {
                      categoryId,
                    },
                  })
                }
                scope={isDarkMode ? 'quaternary' : 'secondary'}
                selected
                value={t('backToDetailCategories')}
              />
            )}
            {isDetailCategoriesPage && loadSummaryData && (
              <CommonButton
                id="back-to-control-tower"
                action={() => {
                  if (setMessage) setMessage('');
                  history.push(path);
                  const params = {
                    dateFrom: rangeDate?.from ? utils.date.formatDateForDb(rangeDate.from) : '',
                    dateTo: rangeDate?.to ? utils.date.formatDateForDb(rangeDate.to) : '',
                    idCompanyList: selectedCompanies?.map(({ value }) => value) || [],
                    idDocTypeList: selectedTypes?.map(({ value }) => value) || [],
                    incChannelList: selectedChannels?.map(({ value }) => value) || [],
                    idGbsList: selectedGbsList?.map(({ value }) => value) || [],
                  };
                  loadSummaryData(params);
                }}
                scope={isDarkMode ? 'quaternary' : 'secondary'}
                selected
                value={t('BackToControlTower')}
              />
            )}
          </>
        )}
        <Toolbar.Right>
          <Toolbar.ModuleName style={{ marginRight: 24 }}>{programName}</Toolbar.ModuleName>
        </Toolbar.Right>
      </Toolbar.BlueBar>
      <Toolbar.ActionBar style={{ paddingRight: 50 }}>
        <Left>
          {filters.some((filter) => filter.filterKey === 'rangeDate' && filter.isVisible) && types.length > 0 && (
            <DateFilters rangeDate={rangeDate} setRangeDate={setRangeDate} />
          )}
          <MultiSelectGroup>
            {filters.some((filter) => filter.filterKey === 'gbsList' && filter.isVisible) && gbsList.length > 0 && (
              <MultiSelectInput
                id="gbsList"
                labelledBy="gbs"
                placeholder={t('filterByGbs')}
                options={utils.input.buildOptions(gbsList, 'gbsName', 'idGbs')}
                value={selectedGbsList || []}
                onChange={onChangeSelectedGbsList}
              />
            )}
            {filters.some((filter) => filter.filterKey === 'companies' && filter.isVisible) && companies.length > 0 && (
              <MultiSelectInput
                id="companies"
                labelledBy="company"
                options={companiesOptions}
                value={selectedCompanies || []}
                onChange={setSelectedCompanies}
                placeholder={t('filterByCompany')}
              />
            )}
            {filters.some((filter) => filter.filterKey === 'types' && filter.isVisible) && types.length > 0 && (
              <MultiSelectInput
                id="types"
                labelledBy="type"
                options={utils.input.buildOptions(types, 'docName', 'idDocType')}
                value={selectedTypes || []}
                onChange={setSelectedTypes}
                placeholder={t('filterByType')}
              />
            )}
            {filters.some((filter) => filter.filterKey === 'channels' && filter.isVisible) && channels.length > 0 && (
              <MultiSelectInput
                id="channels"
                labelledBy="channel"
                options={channels}
                value={selectedChannels || []}
                onChange={setSelectedChannels}
                placeholder={t('filterByChannel')}
              />
            )}
          </MultiSelectGroup>
          <TooltipControlled content={t('search')} trigger="hover" placement="bottom" delayShow={delayShow}>
            <CommonButton
              id="ct-search-button"
              scope="primary"
              selected={isDarkMode ? true : false}
              action={handleSearch}
              disabled={!isSearchEnabled}
              customIcon="search"
            />
          </TooltipControlled>
          <TooltipControlled
            content={utils.app.textTranslated('Default-filters', t, 'Default filters')}
            trigger="hover"
            placement="bottom"
            delayShow={delayShow}
          >
            <CommonButton
              id="ct-restore-default-filters-button"
              action={handleRestoreDefaultFilters}
              scope={isDarkMode ? 'secondary' : 'secondary'}
              customIcon="undo-alt"
            />
          </TooltipControlled>
          <TooltipControlled content={t('clear')} trigger="hover" placement="bottom" delayShow={delayShow}>
            <CommonButton
              id="ct-clear-button"
              action={handleClear}
              scope={isDarkMode ? 'secondary' : 'secondary'}
              customIcon="trash"
            />
          </TooltipControlled>
        </Left>
      </Toolbar.ActionBar>
    </ToolbarWrapper>
  );
};

export default Header;
